import { Button, Checkbox, Divider, Row, Tooltip } from "antd";
import { useEffect, useMemo, useState } from "react";
import get from "lodash/get";

import { fetcher } from "../../data/fetcher";
import { useNotification } from "contexts/notification/use-notification";
import { InfoCard } from "../../components/info-card";
import {
  usePatchMicrosoftFeatures,
  useMicrosoftLogout,
} from "../../data/use-integrations";

export const MicrosoftConfig = ({ profile }) => {
  const notification = useNotification();
  const { email, isConfigured, settings } =
    get(profile, "integrations.microsoft") || {};

  const features = settings?.features || {};
  const emailSettings = features?.email || {};
  const calendarSettings = features?.calendar || {};

  const emailEnabled = emailSettings?.isEnabled ?? true;
  const calendarPermission = calendarSettings?.permission;
  const calendarEnabled =
    calendarPermission === "read" || calendarPermission === "write";
  const calendarWriteEnabled = calendarPermission === "write";

  const emailPublic = emailSettings?.isPublic ?? false;
  const calendarPublic = !!calendarSettings?.isPublic;

  const [isLoading, setIsLoading] = useState();

  // Step 1 (not connected): feature selection to request scopes.
  // Defaults: Email checked, Calendar read checked.
  const [connectEmailEnabled, setConnectEmailEnabled] = useState(true);
  const [connectCalendarReadEnabled, setConnectCalendarReadEnabled] =
    useState(true);
  const [connectCalendarWriteEnabled, setConnectCalendarWriteEnabled] =
    useState(false);

  // Step 2 (connected): draft feature selection that requires reconnect.
  const [reconnectEmailEnabled, setReconnectEmailEnabled] = useState(
    !!emailEnabled
  );
  const [reconnectCalendarReadEnabled, setReconnectCalendarReadEnabled] =
    useState(!!calendarEnabled);
  const [reconnectCalendarWriteEnabled, setReconnectCalendarWriteEnabled] =
    useState(!!calendarWriteEnabled);

  useEffect(() => {
    if (!isConfigured) return;
    setReconnectEmailEnabled(!!emailEnabled);
    setReconnectCalendarReadEnabled(!!calendarEnabled);
    setReconnectCalendarWriteEnabled(!!calendarWriteEnabled);
  }, [isConfigured, emailEnabled, calendarEnabled, calendarWriteEnabled]);

  const authenticate = async ({
    nextEmailEnabled,
    nextCalendarReadEnabled,
    nextCalendarWriteEnabled,
  } = {}) => {
    setIsLoading(true);

    try {
      const query = {
        // default behavior enables email unless explicitly disabled
        email: nextEmailEnabled === false ? "false" : "true",
        calendar: nextCalendarWriteEnabled
          ? "write"
          : nextCalendarReadEnabled
          ? "read"
          : "off",
      };

      const { url } = await fetcher.get("/integrations/microsoft-graph/oauth", {
        query,
      })();

      window.location.href = url;
    } catch (err) {
      notification.support(err);
    }

    setIsLoading(false);
  };

  const patchFeatures = usePatchMicrosoftFeatures();
  const logout = useMicrosoftLogout();

  const reconnectDisabledReason = useMemo(() => {
    const noneSelected =
      !reconnectEmailEnabled && !reconnectCalendarReadEnabled;
    if (noneSelected) return "Select Email or Calendar to reconnect.";

    return null;
  }, [
    reconnectEmailEnabled,
    reconnectCalendarReadEnabled,
    emailEnabled,
    calendarEnabled,
  ]);

  return (
    <Row gutter={16} id="microsoft-config-card" type="flex">
      <InfoCard
        content={
          <>
            <div style={{ margin: "8px 0px" }}>
              Integrating your email allows CRM read-only access to link your
              email interactions to your contacts, accounts, and opportunities.
              This makes analyzing and acting on your opportunities easier.
            </div>
            <div style={{ margin: "16px 0px" }}>
              We use Microsoft Graph for secure and direct email integration.
              You will be prompted to sign in to your Microsoft account.
            </div>

            <Divider />
            {isConfigured ? (
              <>
                <div style={{ margin: "16px 0px" }}>
                  Your email integration has been successfully configured with{" "}
                  <strong>{email}</strong>
                </div>

                <div style={{ margin: "24px 0px 8px" }}>
                  <strong>
                    Would you like to make any of this info public to your team?
                  </strong>
                </div>
                <div style={{ marginBottom: "12px" }}>
                  <Checkbox
                    checked={!!emailPublic}
                    disabled={patchFeatures.isLoading}
                    onChange={(e) =>
                      patchFeatures.mutate({
                        features: {
                          email: { isPublic: e.target.checked },
                        },
                      })
                    }
                  >
                    Email
                  </Checkbox>
                </div>

                <div style={{ marginBottom: "24px" }}>
                  <Tooltip
                    title={!calendarEnabled ? "Connect calendar first" : null}
                    placement="top"
                  >
                    <span>
                      <Checkbox
                        checked={!!calendarPublic}
                        disabled={patchFeatures.isLoading || !calendarEnabled}
                        onChange={(e) =>
                          patchFeatures.mutate({
                            features: {
                              calendar: { isPublic: e.target.checked },
                            },
                          })
                        }
                      >
                        Calendar
                      </Checkbox>
                    </span>
                  </Tooltip>
                </div>

                <Divider />

                <div style={{ margin: "16px 0px 8px" }}>
                  <strong>Connection settings</strong>
                </div>
                <div style={{ marginBottom: "8px" }}>
                  Select what you want connected. Changes require reconnect.
                </div>

                <div style={{ marginBottom: "12px" }}>
                  <Checkbox
                    checked={!!reconnectEmailEnabled}
                    onChange={(e) => setReconnectEmailEnabled(e.target.checked)}
                  >
                    Email
                  </Checkbox>
                </div>
                <div style={{ marginBottom: "12px" }}>
                  <Checkbox
                    checked={!!reconnectCalendarReadEnabled}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setReconnectCalendarReadEnabled(checked);
                      if (!checked) setReconnectCalendarWriteEnabled(false);
                    }}
                  >
                    Calendar (read)
                  </Checkbox>
                </div>
                <div style={{ marginBottom: "16px" }}>
                  <Checkbox
                    checked={!!reconnectCalendarWriteEnabled}
                    disabled={!reconnectCalendarReadEnabled}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setReconnectCalendarWriteEnabled(checked);
                      if (checked) setReconnectCalendarReadEnabled(true);
                    }}
                  >
                    Calendar (write)
                  </Checkbox>
                </div>

                <div style={{ display: "flex", gap: 12 }}>
                  <Tooltip
                    title={reconnectDisabledReason}
                    placement="top"
                    open={!!reconnectDisabledReason ? undefined : false}
                  >
                    <span>
                      <Button
                        id="microsoft-config-reconnect-button"
                        disabled={!!reconnectDisabledReason}
                        loading={isLoading}
                        onClick={() =>
                          authenticate({
                            nextEmailEnabled: reconnectEmailEnabled,
                            nextCalendarReadEnabled:
                              reconnectCalendarReadEnabled,
                            nextCalendarWriteEnabled:
                              reconnectCalendarWriteEnabled,
                          })
                        }
                        type="primary"
                      >
                        Reconnect
                      </Button>
                    </span>
                  </Tooltip>

                  <Button
                    id="microsoft-disconnect-button"
                    loading={logout.isLoading}
                    onClick={() => logout.mutate()}
                  >
                    Disconnect
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div style={{ margin: "16px 0px 8px" }}>
                  <strong>What would you like to connect?</strong>
                </div>
                <div style={{ marginBottom: "12px" }}>
                  <Checkbox
                    checked={!!connectEmailEnabled}
                    onChange={(e) => setConnectEmailEnabled(e.target.checked)}
                  >
                    Email
                  </Checkbox>
                </div>
                <div style={{ marginBottom: "12px" }}>
                  <Checkbox
                    checked={!!connectCalendarReadEnabled}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setConnectCalendarReadEnabled(checked);
                      if (!checked) setConnectCalendarWriteEnabled(false);
                    }}
                  >
                    Calendar (read)
                  </Checkbox>
                </div>
                <div style={{ marginBottom: "12px" }}>
                  <Checkbox
                    checked={!!connectCalendarWriteEnabled}
                    disabled={!connectCalendarReadEnabled}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setConnectCalendarWriteEnabled(checked);
                      if (checked) setConnectCalendarReadEnabled(true);
                    }}
                  >
                    Calendar (write)
                  </Checkbox>
                </div>
                <div style={{ marginBottom: "16px" }}>
                  These will show up in the Activity Feed where applicable.
                </div>

                <Tooltip
                  title={
                    !connectEmailEnabled && !connectCalendarReadEnabled
                      ? "Select Email or Calendar to connect."
                      : null
                  }
                  placement="top"
                >
                  <span>
                    <Button
                      id="microsoft-config-connect-button"
                      disabled={
                        !connectEmailEnabled && !connectCalendarReadEnabled
                      }
                      loading={isLoading}
                      onClick={() =>
                        authenticate({
                          nextEmailEnabled: connectEmailEnabled,
                          nextCalendarReadEnabled: connectCalendarReadEnabled,
                          nextCalendarWriteEnabled: connectCalendarWriteEnabled,
                        })
                      }
                      type="primary"
                    >
                      Connect
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
          </>
        }
        label={
          <div style={{ marginBottom: "16px" }}>
            <span style={{ fontSize: "16px" }}>Microsoft Integration</span>
          </div>
        }
      />
    </Row>
  );
};
